import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Download, 
  ChevronLeft, 
  ChevronRight, 
  Play,
  Pause,
  RotateCcw,
  Mail,
  Shield,
  GraduationCap,
  Check,
  Smartphone,
  ListChecks
} from 'lucide-react';

const RegistrationGuide = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);

  const steps = [
    {
      id: 1,
      title: "Visit Eduzy Website",
      description: "Go to eduzy.co and click on 'Start Free Trial' button",
      icon: Smartphone,
      color: "bg-emerald-500",
      details: [
        "Open your browser (Chrome, Safari, or any other)",
        "Type eduzy.co in the address bar",
        "You'll see the Eduzy homepage",
        "Click on 'Start Free Trial' button on the home page"
      ],
      tip: "Bookmark the page for easy access later!"
    },
    {
      id: 2,
      title: "Enter Your Details",
      description: "Fill in your name and email address",
      icon: Mail,
      color: "bg-blue-500",
      details: [
        "Enter your full name",
        "Enter your email address (you'll receive OTP here)",
        "Click 'Continue' to proceed",
        "A verification code will be sent to your email"
      ],
      tip: "Use an email you check regularly for notifications"
    },
    {
      id: 3,
      title: "Verify Email with OTP",
      description: "Enter the 6-digit code sent to your email",
      icon: Shield,
      color: "bg-amber-500",
      details: [
        "Check your email inbox (and spam folder)",
        "Find the email from Eduzy with subject 'Verification Code'",
        "Enter the 6-digit code in the form",
        "Click 'Verify' to continue"
      ],
      tip: "Code expires in 10 minutes. Click 'Resend' if needed"
    },
    {
      id: 4,
      title: "Add Child Information",
      description: "Enter your child's details and select subjects",
      icon: GraduationCap,
      color: "bg-cyan-500",
      details: [
        "Enter your child's name",
        "Select their grade (5th to 8th)",
        "Optionally add school name",
        "Select subjects they want to learn (Math, Science, English, etc.)",
        "Click 'Complete & Start Free Trial' to finish"
      ],
      tip: "You can add more children later from the dashboard"
    },
    {
      id: 5,
      title: "Login & Select Syllabus Topics",
      description: "Complete registration by choosing specific topics for personalized learning",
      icon: ListChecks,
      color: "bg-emerald-600",
      details: [
        "After registration, go to the Parent Login page",
        "Login with your email and password",
        "You'll be prompted to select syllabus topics for each subject",
        "Example: For Math - select Fractions, Algebra, Geometry",
        "Example: For Science - select Physics, Chemistry, Biology",
        "This enables the AI to generate relevant, targeted content for your child"
      ],
      tip: "Select topics your child finds challenging for focused practice. Your child can start learning once topics are selected!"
    }
  ];

  const startAutoPlay = () => {
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= steps.length - 1) {
          clearInterval(intervalRef.current);
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 4000);
  };

  const stopAutoPlay = () => {
    setIsPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const resetGuide = () => {
    stopAutoPlay();
    setCurrentStep(0);
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const downloadGuide = () => {
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Eduzy - Parent Registration Guide</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: linear-gradient(135deg, #f0fdf4 0%, #ecfeff 100%); min-height: 100vh; padding: 40px 20px; }
    .container { max-width: 800px; margin: 0 auto; }
    .header { text-align: center; margin-bottom: 40px; }
    .header h1 { color: #10b981; font-size: 2.5rem; margin-bottom: 10px; }
    .header p { color: #6b7280; font-size: 1.1rem; }
    .step { background: white; border-radius: 16px; padding: 24px; margin-bottom: 24px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); border-left: 4px solid #10b981; }
    .step-header { display: flex; align-items: center; gap: 16px; margin-bottom: 16px; }
    .step-number { width: 40px; height: 40px; background: #10b981; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 1.2rem; }
    .step-title { font-size: 1.3rem; font-weight: 600; color: #1f2937; }
    .step-desc { color: #6b7280; margin-bottom: 16px; font-size: 1rem; }
    .step-details { background: #f9fafb; border-radius: 8px; padding: 16px; }
    .step-details ul { list-style: none; }
    .step-details li { padding: 8px 0; padding-left: 24px; position: relative; color: #374151; }
    .step-details li:before { content: "✓"; position: absolute; left: 0; color: #10b981; font-weight: bold; }
    .tip { background: #fef3c7; border-radius: 8px; padding: 12px 16px; margin-top: 16px; font-size: 0.9rem; color: #92400e; }
    .tip:before { content: "💡 Tip: "; font-weight: 600; }
    .footer { text-align: center; margin-top: 40px; padding: 20px; background: #10b981; color: white; border-radius: 16px; }
    .footer h3 { margin-bottom: 10px; }
    .footer p { opacity: 0.9; }
    @media print { body { background: white; } .step { break-inside: avoid; } }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>📚 Eduzy Registration Guide</h1>
      <p>Follow these simple steps to register your child for AI-powered WhatsApp learning</p>
    </div>
    
    ${steps.map((step, index) => `
    <div class="step">
      <div class="step-header">
        <div class="step-number">${index + 1}</div>
        <div class="step-title">${step.title}</div>
      </div>
      <div class="step-desc">${step.description}</div>
      <div class="step-details">
        <ul>
          ${step.details.map(detail => `<li>${detail}</li>`).join('')}
        </ul>
      </div>
      <div class="tip">${step.tip}</div>
    </div>
    `).join('')}
    
    <div class="footer">
      <h3>Need Help?</h3>
      <p>Contact us at support@contentiumtech.com or visit our FAQ page</p>
      <p style="margin-top: 10px; font-size: 0.9rem;">© 2025 Eduzy by Contentium Tech</p>
    </div>
  </div>
</body>
</html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Eduzy_Registration_Guide.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const currentStepData = steps[currentStep];
  const IconComponent = currentStepData.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-cyan-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Eduzy</h1>
              <p className="text-sm text-slate-500">Registration Guide</p>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <a 
              href="/"
              className="text-sm text-slate-600 hover:text-emerald-600 transition-colors hidden sm:block"
            >
              ← Back to Home
            </a>
            <Button 
              onClick={downloadGuide}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="download-guide-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Guide
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">Step {currentStep + 1} of {steps.length}</span>
            <span className="text-sm text-slate-500">{Math.round(((currentStep + 1) / steps.length) * 100)}% Complete</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Indicators */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {steps.map((step, index) => (
            <button
              key={step.id}
              onClick={() => setCurrentStep(index)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                index === currentStep 
                  ? 'bg-emerald-500 text-white scale-110' 
                  : index < currentStep 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-slate-100 text-slate-500'
              }`}
              data-testid={`step-indicator-${index + 1}`}
            >
              {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
            </button>
          ))}
        </div>

        {/* Current Step Card */}
        <Card className="overflow-hidden shadow-xl mb-8">
          <div className={`${currentStepData.color} p-8 text-white`}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <IconComponent className="w-8 h-8" />
              </div>
              <div>
                <div className="text-white/70 text-sm font-medium">Step {currentStep + 1}</div>
                <h2 className="text-2xl font-bold">{currentStepData.title}</h2>
              </div>
            </div>
            <p className="text-white/90 text-lg">{currentStepData.description}</p>
          </div>
          
          <div className="p-8">
            <div className="space-y-4 mb-6">
              {currentStepData.details.map((detail, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>
                  <p className="text-slate-700">{detail}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-800">
                <span className="font-semibold">💡 Tip:</span> {currentStepData.tip}
              </p>
            </div>
          </div>
        </Card>

        {/* Navigation Controls */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
            className="gap-2"
            data-testid="prev-step-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={resetGuide}
              title="Reset"
              data-testid="reset-btn"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={isPlaying ? stopAutoPlay : startAutoPlay}
              title={isPlaying ? "Pause" : "Auto Play"}
              data-testid="autoplay-btn"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
          </div>
          
          <Button
            onClick={nextStep}
            disabled={currentStep === steps.length - 1}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            data-testid="next-step-btn"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Footer CTA */}
        <div className="mt-12 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-2">Ready to Get Started?</h3>
          <p className="text-white/90 mb-6">Give your child the gift of personalized AI learning on WhatsApp</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="bg-white text-emerald-600 hover:bg-emerald-50"
              onClick={() => window.location.href = '/'}
            >
              Start Free Trial
            </Button>
            <Button 
              variant="outline" 
              className="border-white text-white hover:bg-white/10"
              onClick={downloadGuide}
            >
              <Download className="w-4 h-4 mr-2" />
              Download This Guide
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationGuide;
